/** 本地开发辅助输出，生产构建不会打印。 */
import { getEnv } from './env'

const SDK_DEBUG_FLAG = '__XIAOWEI_WEB_SDK_DEBUG__'
const MAX_TRACE_LOGS = 300
const MAX_DETAIL_TEXT_LENGTH = 4000

export type TraceLogLevel = 'info' | 'warn' | 'error'

/**
 * 日志详细度档位：过滤 info 级别日志在面板的可见性。
 * - simple：只显示关键生命周期和用户操作（连接、错误、UI 点击、语音识别/合成事件）。
 * - medium：额外显示 store/speech/session/recorder 等中间态生命周期日志。
 * - all：显示全部，包括播放器每帧解码与 frames 帧快照。
 * warn/error 级别始终显示，不受档位影响。
 */
export type TraceVerbosity = 'simple' | 'medium' | 'all'

const TRACE_VERBOSITY_KEY = 'xiaowei.traceVerbosity'

// simple 档可见的 info scope：用户可见的生命周期里程碑与关键事件。
const SIMPLE_SCOPES = new Set([
  'boot',
  'ua',
  'sdk', // 连接 ready/error/closed/goodbye 等核心里程碑
  'ui',
  'listener', // sessionState/userInputCommitted/assistantSentence 等对话负载
  'retry',
  'emotion',
  'token',
  'transport',
  'window.error',
  'window.unhandledrejection',
  'sdk.stt',
  'sdk.tts',
  'sdk.llm',
  'sdk.mcp',
  'sdk.tool',
])

// medium 档在 simple 基础上额外显示的 info scope：内部状态机与中间生命周期。
const MEDIUM_EXTRA_SCOPES = new Set([
  'store',
  'speech',
  'session',
  'recorder',
  'sdk.audio',
])

export interface TraceLogEntry {
  id: number
  timestamp: number
  level: TraceLogLevel
  scope: string
  message: string
  detailText?: string
}

type TraceLogListener = (entries: TraceLogEntry[]) => void

let nextTraceLogId = 0
let traceLogs: TraceLogEntry[] = []
const traceLogListeners = new Set<TraceLogListener>()

// 当前日志详细度档位，从 localStorage 恢复；默认 simple 避免 player 等高频 scope 刷屏。
let traceVerbosity: TraceVerbosity = loadTraceVerbosity()

function loadTraceVerbosity(): TraceVerbosity {
  try {
    const raw = localStorage.getItem(TRACE_VERBOSITY_KEY)
    if (raw === 'simple' || raw === 'medium' || raw === 'all') return raw
  } catch {
    /* 隐私模式或无 localStorage：用默认 simple */
  }
  return 'simple'
}

export function getTraceVerbosity(): TraceVerbosity {
  return traceVerbosity
}

export function setTraceVerbosity(next: TraceVerbosity): void {
  if (next === traceVerbosity) return
  traceVerbosity = next
  try {
    localStorage.setItem(TRACE_VERBOSITY_KEY, next)
  } catch {
    /* 隐私模式或 quota：忽略，不影响 UI */
  }
  // 档位只影响后续推入的日志是否入 ring buffer，
  // 已存储的历史日志不变，故无需通知订阅者重算。
}

/**
 * 判断单条日志在当前详细度档位下是否应推入 ring buffer。
 * warn/error 始终存储；info 按 scope 白名单过滤——
 * 不匹配的直接不 push，避免高频 scope（player/frames）占满 300 条 buffer 把关键日志挤掉。
 */
function shouldStoreTraceEntry(level: TraceLogLevel, scope: string): boolean {
  if (level !== 'info') return true
  if (traceVerbosity === 'all') return true
  if (SIMPLE_SCOPES.has(scope)) return true
  if (traceVerbosity === 'medium' && MEDIUM_EXTRA_SCOPES.has(scope)) return true
  return false
}

export function isInternalTraceEnabled(): boolean {
  if (getEnv('INTERNAL_TRACE') !== 'true') {
    return false
  }
  if (import.meta.env.DEV) {
    return true
  }
  return isInternalTraceRouteEnabled()
}

function isInternalTraceRouteEnabled(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  const params = new URLSearchParams(window.location.search)
  return params.getAll('inmote').includes('xw')
}

export interface DevFrameRow {
  timestamp: number
  direction: string
  kind: string
  summary: string
  bytes?: number
}

export function initDevTrace(summary: Record<string, unknown>): void {
  const enabled = isInternalTraceEnabled()
  const globalFlags = globalThis as typeof globalThis & Record<string, unknown>
  globalFlags[SDK_DEBUG_FLAG] = enabled
  if (!enabled) return

  installSdkConsoleCapture()

  pushTraceLog('info', 'boot', 'dev trace enabled', summary)
  pushTraceLog('info', 'boot', 'SDK trace enabled via global flag')
  console.groupCollapsed(withTraceTimestamp('[xiaowei-demo][boot] dev trace enabled'))
  console.table(summary)
  console.info(withTraceTimestamp('[xiaowei-demo][boot] SDK trace enabled via global flag'))
  console.groupEnd()
}

/**
 * 劫持 console.info / console.warn / console.error，把客户端前缀的输出转发到 trace 面板。
 *
 * Why：默认只有 emit 事件进 trace 面板，console 输出的诊断日志看不到。
 * 这里捕获所有带客户端日志前缀的输出，避免真机排查时漏掉关键 info 级诊断。
 */
function installSdkConsoleCapture(): void {
  const SDK_PREFIX = '[xiaowei-web-sdk]'
  const capture = (level: TraceLogLevel, original: (...args: unknown[]) => void) => {
    return (...args: unknown[]): void => {
      try {
        const first = args[0]
        if (typeof first === 'string' && first.includes(SDK_PREFIX)) {
          // SDK 日志格式：[timestamp] [xiaowei-web-sdk][scope] message fields
          // 直接定位 SDK 前缀位置，避免误把 [xiaowei-web-sdk] 当时间戳吃掉。
          const after = first.slice(first.indexOf(SDK_PREFIX) + SDK_PREFIX.length)
          const scopeMatch = after.match(/^\[([^\]]*)\]\s*/)
          const scope = scopeMatch?.[1] ?? 'sdk'
          const message = scopeMatch ? after.slice(scopeMatch[0].length) : after.trim()
          const detail = args.length > 1 ? args.slice(1) : undefined
          pushTraceLog(level, scope, message, detail)
        }
      } catch {
        /* 解析失败不影响原 console 输出 */
      }
      original.apply(console, args)
    }
  }
  console.info = capture('info', console.info.bind(console))
  console.warn = capture('warn', console.warn.bind(console))
  console.error = capture('error', console.error.bind(console))
}

export function demoTrace(scope: string, message: string, detail?: unknown): void {
  if (!isInternalTraceEnabled()) {
    return
  }
  pushTraceLog('info', scope, message, detail)
  if (detail === undefined) {
    console.info(withTraceTimestamp(`[xiaowei-demo][${scope}] ${message}`))
    return
  }
  console.info(withTraceTimestamp(`[xiaowei-demo][${scope}] ${message}`), detail)
}

export function dumpFrameSnapshot(label: string, rows: DevFrameRow[]): void {
  if (!isInternalTraceEnabled() || rows.length === 0) {
    return
  }
  pushTraceLog('info', 'frames', `${label} (${rows.length})`, rows)
  console.groupCollapsed(withTraceTimestamp(`[xiaowei-demo][frames] ${label} (${rows.length})`))
  console.table(
    rows.map(row => ({
      t_ms: Number(row.timestamp.toFixed(1)),
      dir: row.direction,
      kind: row.kind,
      bytes: row.bytes ?? '',
      summary: row.summary,
    })),
  )
  console.groupEnd()
}

export function getTraceLogs(): TraceLogEntry[] {
  return traceLogs.slice()
}

export function subscribeTraceLogs(listener: TraceLogListener): () => void {
  traceLogListeners.add(listener)
  listener(getTraceLogs())
  return () => {
    traceLogListeners.delete(listener)
  }
}

export function clearTraceLogs(): void {
  traceLogs = []
  notifyTraceLogListeners()
}

function pushTraceLog(level: TraceLogLevel, scope: string, message: string, detail?: unknown): void {
  // 源头过滤：不匹配当前档位的 info 日志直接丢弃，不进 ring buffer。
  // 这样 player 等高频 scope 在 simple/medium 档下不会占满 300 条把关键日志挤掉。
  if (!shouldStoreTraceEntry(level, scope)) return
  const entry: TraceLogEntry = {
    id: ++nextTraceLogId,
    timestamp: Date.now(),
    level,
    scope,
    message,
    detailText: detail === undefined ? undefined : formatTraceDetail(detail),
  }
  traceLogs = [...traceLogs, entry].slice(-MAX_TRACE_LOGS)
  notifyTraceLogListeners()
}

function notifyTraceLogListeners(): void {
  const snapshot = getTraceLogs()
  traceLogListeners.forEach(listener => listener(snapshot))
}

function formatTraceDetail(detail: unknown): string {
  try {
    const text = JSON.stringify(detail, traceDetailReplacer, 2)
    return text.length > MAX_DETAIL_TEXT_LENGTH
      ? `${text.slice(0, MAX_DETAIL_TEXT_LENGTH)}\n... truncated`
      : text
  } catch {
    return String(detail)
  }
}

function traceDetailReplacer(_key: string, value: unknown): unknown {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    }
  }
  return value
}

function withTraceTimestamp(message: string): string {
  return `[${formatTraceTimestamp()}] ${message}`
}

export function formatTraceTimestamp(date = new Date()): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hour = String(date.getHours()).padStart(2, '0')
  const minute = String(date.getMinutes()).padStart(2, '0')
  const second = String(date.getSeconds()).padStart(2, '0')
  const millisecond = String(date.getMilliseconds()).padStart(3, '0')
  return `${year}-${month}-${day} ${hour}:${minute}:${second}.${millisecond}`
}
